import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './CourierProcessingFeeSettings.css';

/**
 * Courier Processing Fee settings panel.
 *
 * A merchant-configured courier processing fee REPLACES the equivalent generic
 * processing charge TDK would otherwise show. It is never added on top of it.
 * Customs duty, import VAT/GST and other statutory charges are unaffected.
 *
 * The backend is the single source of truth for eligibility: it fails closed
 * when the destination, presentment currency or consignment count cannot be
 * established, so the panel never promises an amount the engine will not use.
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;


const FALLBACK_CURRENCIES = ['EUR', 'GBP', 'USD', 'NOK', 'SEK', 'DKK', 'CHF', 'CAD', 'AUD', 'JPY'];

const FALLBACK_DESTINATIONS = [
  { value: 'AT', label: 'Austria', group: 'European Union' },
  { value: 'BE', label: 'Belgium', group: 'European Union' },
  { value: 'BG', label: 'Bulgaria', group: 'European Union' },
  { value: 'HR', label: 'Croatia', group: 'European Union' },
  { value: 'CY', label: 'Cyprus', group: 'European Union' },
  { value: 'CZ', label: 'Czech Republic', group: 'European Union' },
  { value: 'DK', label: 'Denmark', group: 'European Union' },
  { value: 'EE', label: 'Estonia', group: 'European Union' },
  { value: 'FI', label: 'Finland', group: 'European Union' },
  { value: 'FR', label: 'France', group: 'European Union' },
  { value: 'DE', label: 'Germany', group: 'European Union' },
  { value: 'GR', label: 'Greece', group: 'European Union' },
  { value: 'HU', label: 'Hungary', group: 'European Union' },
  { value: 'IE', label: 'Ireland', group: 'European Union' },
  { value: 'IT', label: 'Italy', group: 'European Union' },
  { value: 'LV', label: 'Latvia', group: 'European Union' },
  { value: 'LT', label: 'Lithuania', group: 'European Union' },
  { value: 'LU', label: 'Luxembourg', group: 'European Union' },
  { value: 'MT', label: 'Malta', group: 'European Union' },
  { value: 'NL', label: 'Netherlands', group: 'European Union' },
  { value: 'PL', label: 'Poland', group: 'European Union' },
  { value: 'PT', label: 'Portugal', group: 'European Union' },
  { value: 'RO', label: 'Romania', group: 'European Union' },
  { value: 'SK', label: 'Slovakia', group: 'European Union' },
  { value: 'SI', label: 'Slovenia', group: 'European Union' },
  { value: 'ES', label: 'Spain', group: 'European Union' },
  { value: 'SE', label: 'Sweden', group: 'European Union' },
  { value: 'GB', label: 'United Kingdom', group: 'Other' },
  { value: 'NO', label: 'Norway', group: 'Other' },
  { value: 'CH', label: 'Switzerland', group: 'Other' },
  { value: 'US', label: 'United States', group: 'Other' },
  { value: 'CA', label: 'Canada', group: 'Other' },
  { value: 'AU', label: 'Australia', group: 'Other' },
  { value: 'NZ', label: 'New Zealand', group: 'Other' },
  { value: 'JP', label: 'Japan', group: 'Other' },
  { value: 'SG', label: 'Singapore', group: 'Other' },
];

const CARRIERS = [
  { value: 'dhl', label: 'DHL Express' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'other', label: 'Other (custom name)' },
];

const EMPTY_FORM = {
  enabled: false,
  carrier: 'dhl',
  other_carrier_name: '',
  amount: '',
  currency: 'EUR',
  charge_basis: 'per_consignment',
  destination_countries: [],
  exact_currency_amounts: {},
};

const normaliseAmount = (value) => {
  const parsed = Number.parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed.toFixed(2) : '';
};

const CourierProcessingFeeSettings = ({ shop }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [extraAmounts, setExtraAmounts] = useState([]);
  const [options, setOptions] = useState({
    destinations: FALLBACK_DESTINATIONS,
    currencies: FALLBACK_CURRENCIES,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [testInput, setTestInput] = useState({
    destination_country: 'DE',
    presentment_currency: 'EUR',
    goods_value: '100.00',
    consignment_count: '1',
  });
  const [testResult, setTestResult] = useState(null);
  const [showAllDestinations, setShowAllDestinations] = useState(false);

  const authenticatedHeaders = useCallback(async () => {
    if (!window.shopify || typeof window.shopify.idToken !== 'function') {
      throw new Error('Shopify Admin session is unavailable. Please reopen the app in Shopify Admin.');
    }
    const token = await window.shopify.idToken();
    if (!token) throw new Error('Shopify Admin session token is unavailable.');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }, []);

  const applyLoadedConfig = useCallback((config) => {
    if (!config) {
      setForm(EMPTY_FORM);
      setExtraAmounts([]);
      return;
    }
    setForm({
      enabled: Boolean(config.enabled),
      carrier: config.carrier || 'dhl',
      other_carrier_name: config.other_carrier_name || '',
      amount: config.amount != null ? String(config.amount) : '',
      currency: config.currency || 'EUR',
      charge_basis: config.charge_basis || 'per_consignment',
      destination_countries: config.destination_countries || [],
      exact_currency_amounts: config.exact_currency_amounts || {},
    });
    setExtraAmounts(
      Object.entries(config.exact_currency_amounts || {}).map(([currency, amount]) => ({
        currency,
        amount: String(amount),
      }))
    );
  }, []);

  const fetchOptions = useCallback(async () => {
    if (!API_BASE_URL) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/courier-processing-fee/options`, {
        headers: await authenticatedHeaders(),
      });
      if (!response.ok) return;
      const data = await response.json();
      const groups = {};
      (data.destinations || []).forEach((item) => {
        const group = item.group || 'Other';
        if (!groups[group]) groups[group] = [];
        groups[group].push({
          value: item.value,
          label: item.label && item.label !== item.value ? `${item.label} (${item.value})` : item.value,
        });
      });
      const destinations = Object.entries(groups).flatMap(([group, items]) =>
        items.map((item) => ({ ...item, group }))
      );
      setOptions({
        destinations: destinations.length ? destinations : FALLBACK_DESTINATIONS,
        currencies: data.default_currencies?.length ? data.default_currencies : FALLBACK_CURRENCIES,
      });
    } catch (err) {
      console.warn('[CourierFee] Could not load option lists, using local defaults', err);
    }
  }, [authenticatedHeaders]);

  const fetchConfig = useCallback(async () => {
    if (!shop || !API_BASE_URL) {
      setLoading(false);
      setError('Shop parameter is missing. Please ensure the app is loaded within Shopify Admin.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/api/courier-processing-fee/config?shop=${encodeURIComponent(shop)}`,
        { headers: await authenticatedHeaders() }
      );
      if (!response.ok) throw new Error(`API Error: ${response.status}`);
      const data = await response.json();
      setConfigured(Boolean(data.configured));
      setUpdatedAt(data.updated_at || null);
      applyLoadedConfig(data.config);
    } catch (err) {
      console.error('[CourierFee] Failed to load configuration', err);
      setError(err.message || 'Failed to load courier processing fee settings');
    } finally {
      setLoading(false);
    }
  }, [applyLoadedConfig, authenticatedHeaders, shop]);

  useEffect(() => {
    fetchOptions();
    fetchConfig();
  }, [fetchConfig, fetchOptions]);

  const buildPayload = () => {
    const exact = {};
    extraAmounts.forEach((row) => {
      const code = (row.currency || '').toUpperCase().trim();
      const amount = normaliseAmount(row.amount);
      if (code && code !== form.currency && amount) exact[code] = amount;
    });
    return {
      shop_domain: shop,
      enabled: form.enabled,
      carrier: form.carrier,
      other_carrier_name: form.carrier === 'other' ? (form.other_carrier_name || '').trim() : null,
      amount: normaliseAmount(form.amount),
      currency: (form.currency || '').toUpperCase(),
      charge_basis: form.charge_basis,
      destination_countries: form.destination_countries,
      exact_currency_amounts: exact,
    };
  };

  const handleSave = async ({ enableOverride } = {}) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const payload = buildPayload();
      if (typeof enableOverride === 'boolean') payload.enabled = enableOverride;

      const response = await fetch(`${API_BASE_URL}/api/courier-processing-fee/config`, {
        method: 'PUT',
        headers: await authenticatedHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : 'Validation failed.';
        throw new Error(detail);
      }
      setConfigured(true);
      setUpdatedAt(data.updated_at || null);
      if (typeof enableOverride === 'boolean') {
        setForm((prev) => ({ ...prev, enabled: enableOverride }));
      }
      setSuccess(
        payload.enabled
          ? '✅ Courier processing fee saved and enabled.'
          : '✅ Courier processing fee saved. It is not enabled yet.'
      );
      setTimeout(() => setSuccess(null), 6000);
    } catch (err) {
      setError(err.message || 'Failed to save courier processing fee settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setError(null);
      setTestResult(null);
      const payload = {
        ...buildPayload(),
        enabled: true,
        destination_country: (testInput.destination_country || '').toUpperCase(),
        presentment_currency: (testInput.presentment_currency || '').toUpperCase(),
        goods_value: Number.parseFloat(testInput.goods_value || '0') || 0,
        consignment_count: Number.parseInt(testInput.consignment_count, 10) || undefined,
      };
      const response = await fetch(`${API_BASE_URL}/api/courier-processing-fee/test`, {
        method: 'POST',
        headers: await authenticatedHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : 'Test calculation failed.';
        throw new Error(detail);
      }
      setTestResult(data);
    } catch (err) {
      setError(err.message || 'Test calculation failed');
    } finally {
      setTesting(false);
    }
  };

  const toggleDestination = (code) => {
    setForm((prev) => {
      const selected = new Set(prev.destination_countries);
      if (selected.has(code)) selected.delete(code);
      else selected.add(code);
      return { ...prev, destination_countries: Array.from(selected).sort() };
    });
  };

  const selectEuGroup = (destinations) => {
    const eu = destinations.filter((d) => d.group === 'European Union').map((d) => d.value);
    const allSelected = eu.every((code) => form.destination_countries.includes(code));
    setForm((prev) => {
      const selected = new Set(prev.destination_countries);
      eu.forEach((code) => (allSelected ? selected.delete(code) : selected.add(code)));
      return { ...prev, destination_countries: Array.from(selected).sort() };
    });
  };

  const addExtraAmount = () => {
    const used = new Set([form.currency, ...extraAmounts.map((row) => row.currency)]);
    const next = options.currencies.find((code) => !used.has(code)) || 'GBP';
    setExtraAmounts((prev) => [...prev, { currency: next, amount: '' }]);
  };

  const updateExtraAmount = (index, patch) => {
    setExtraAmounts((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const removeExtraAmount = (index) => {
    setExtraAmounts((prev) => prev.filter((_, i) => i !== index));
  };

  const destinationGroups = useMemo(() => {
    const groups = {};
    options.destinations.forEach((item) => {
      const group = item.group || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });
    if (!showAllDestinations) {
      const selected = new Set(form.destination_countries);
      Object.keys(groups).forEach((group) => {
        groups[group] = groups[group].filter(
          (item) => group === 'European Union' || selected.has(item.value)
        );
      });
    }
    return groups;
  }, [form.destination_countries, options.destinations, showAllDestinations]);

  if (loading) {
    return (
      <div className="settings-section courier-fee-section">
        <div className="courier-fee-loading">Loading courier processing fee settings…</div>
      </div>
    );
  }

  const currencyOptions = options.currencies.includes(form.currency)
    ? options.currencies
    : [form.currency, ...options.currencies];

  return (
    <div className="settings-section courier-fee-section">
      <h3>🚚 Courier Processing Fee</h3>
      <p className="courier-fee-description">
        Add the processing or clearance fee your courier charges on imported orders. The amount you
        configure <strong>replaces</strong> the equivalent generic processing charge TDK would
        otherwise show — it is <strong>never added on top of it</strong>. Customs duty, import
        VAT/GST and other statutory charges are calculated exactly as before.
      </p>

      {success && <div className="courier-fee-alert courier-fee-alert-success">{success}</div>}
      {error && <div className="courier-fee-alert courier-fee-alert-error">{error}</div>}

      <div className="courier-fee-status">
        <span className={`courier-fee-badge ${form.enabled ? 'is-on' : 'is-off'}`}>
          {form.enabled ? 'Enabled' : 'Not enabled'}
        </span>
        <span className="courier-fee-status-text">
          {configured
            ? `Configuration saved${updatedAt ? ` · last updated ${new Date(updatedAt).toLocaleString()}` : ''}`
            : 'No configuration saved for this store yet'}
        </span>
      </div>

      <div className="courier-fee-grid">
        <div className="courier-fee-field">
          <label htmlFor="courier-carrier">Courier</label>
          <select
            id="courier-carrier"
            className="courier-fee-input"
            value={form.carrier}
            onChange={(e) => setForm((prev) => ({ ...prev, carrier: e.target.value }))}
          >
            {CARRIERS.map((carrier) => (
              <option key={carrier.value} value={carrier.value}>
                {carrier.label}
              </option>
            ))}
          </select>
        </div>

        {form.carrier === 'other' && (
          <div className="courier-fee-field">
            <label htmlFor="courier-other-name">Courier name</label>
            <input
              id="courier-other-name"
              className="courier-fee-input"
              type="text"
              maxLength={100}
              placeholder="e.g. Regional Courier"
              value={form.other_carrier_name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, other_carrier_name: e.target.value }))
              }
            />
          </div>
        )}

        <div className="courier-fee-field">
          <label htmlFor="courier-amount">Processing fee</label>
          <div className="courier-fee-amount-row">
            <input
              id="courier-amount"
              className="courier-fee-input"
              type="text"
              inputMode="decimal"
              placeholder="17.00"
              value={form.amount}
              onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
            />
            <select
              aria-label="Fee currency"
              className="courier-fee-input courier-fee-currency"
              value={form.currency}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))
              }
            >
              {currencyOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <small className="courier-fee-help">
            The agreed amount in your base fee currency. Exact amounts, not currency conversions.
          </small>
        </div>

        <div className="courier-fee-field">
          <label>Charging basis</label>
          <div className="courier-fee-radio-group">
            <label className="courier-fee-radio">
              <input
                type="radio"
                name="charge-basis"
                value="per_order"
                checked={form.charge_basis === 'per_order'}
                onChange={() => setForm((prev) => ({ ...prev, charge_basis: 'per_order' }))}
              />
              <span>Per order</span>
              <small>One fee per order, whatever ships.</small>
            </label>
            <label className="courier-fee-radio">
              <input
                type="radio"
                name="charge-basis"
                value="per_consignment"
                checked={form.charge_basis === 'per_consignment'}
                onChange={() => setForm((prev) => ({ ...prev, charge_basis: 'per_consignment' }))}
              />
              <span>Per consignment</span>
              <small>
                Multiplied by the number of consignments. If TDK cannot establish that number, the
                fee is not applied.
              </small>
            </label>
          </div>
        </div>
      </div>

      <div className="courier-fee-block">
        <h4>Other currencies (optional)</h4>
        <p className="courier-fee-help">
          Add an exact agreed amount for any additional currency you price in. TDK will not convert
          your fee: if a shopper’s currency is not listed here, the fee is not applied and the
          existing generic processing charge is kept.
        </p>
        {extraAmounts.length > 0 && (
          <div className="courier-fee-extra-list">
            {extraAmounts.map((row, index) => (
              <div className="courier-fee-extra-row" key={`${row.currency}-${index}`}>
                <select
                  aria-label="Alternative currency"
                  className="courier-fee-input"
                  value={row.currency}
                  onChange={(e) => updateExtraAmount(index, { currency: e.target.value })}
                >
                  {options.currencies
                    .filter((code) => code !== form.currency)
                    .map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                </select>
                <input
                  aria-label="Exact amount"
                  className="courier-fee-input"
                  type="text"
                  inputMode="decimal"
                  placeholder="198.00"
                  value={row.amount}
                  onChange={(e) => updateExtraAmount(index, { amount: e.target.value })}
                />
                <button
                  type="button"
                  className="courier-fee-remove"
                  onClick={() => removeExtraAmount(index)}
                  aria-label="Remove currency"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        <button type="button" className="courier-fee-link" onClick={addExtraAmount}>
          + Add another currency
        </button>
      </div>

      <div className="courier-fee-block">
        <div className="courier-fee-block-header">
          <h4>Eligible destinations</h4>
          <button
            type="button"
            className="courier-fee-link"
            onClick={() => setShowAllDestinations((prev) => !prev)}
          >
            {showAllDestinations ? 'Show selected only' : 'Show all countries'}
          </button>
        </div>
        <p className="courier-fee-help">
          Apply this fee only where your courier arrangement makes it applicable. Leave every box
          clear to apply it to all destinations.
        </p>
        {form.destination_countries.length === 0 && (
          <div className="courier-fee-note">No restriction — all destinations are currently eligible.</div>
        )}
        {Object.entries(destinationGroups).map(([group, items]) => (
          <div className="courier-fee-destination-group" key={group}>
            <div className="courier-fee-destination-group-title">
              <span>{group}</span>
              {group === 'European Union' && (
                <button
                  type="button"
                  className="courier-fee-link"
                  onClick={() => selectEuGroup(options.destinations)}
                >
                  {items.every((item) => form.destination_countries.includes(item.value))
                    ? 'Clear EU'
                    : 'Select all EU'}
                </button>
              )}
            </div>
            <div className="courier-fee-destination-grid">
              {items.map((item) => (
                <label className="courier-fee-destination" key={item.value}>
                  <input
                    type="checkbox"
                    checked={form.destination_countries.includes(item.value)}
                    onChange={() => toggleDestination(item.value)}
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="courier-fee-actions">
        <button
          type="button"
          className="courier-fee-button courier-fee-button-primary"
          onClick={() => handleSave()}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save configuration'}
        </button>
        {!form.enabled ? (
          <button
            type="button"
            className="courier-fee-button courier-fee-button-primary"
            onClick={() => handleSave({ enableOverride: true })}
            disabled={saving || !form.amount}
          >
            Save &amp; enable
          </button>
        ) : (
          <button
            type="button"
            className="courier-fee-button courier-fee-button-secondary"
            onClick={() => handleSave({ enableOverride: false })}
            disabled={saving}
          >
            Disable
          </button>
        )}
      </div>

      <div className="courier-fee-test">
        <h4>Test calculation</h4>
        <p className="courier-fee-help">
          Runs the real calculation rules without saving or charging anything. This shows the
          generic processing charge that would be replaced.
        </p>
        <div className="courier-fee-grid">
          <div className="courier-fee-field">
            <label htmlFor="test-destination">Destination</label>
            <select
              id="test-destination"
              className="courier-fee-input"
              value={testInput.destination_country}
              onChange={(e) =>
                setTestInput((prev) => ({ ...prev, destination_country: e.target.value }))
              }
            >
              {options.destinations.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="courier-fee-field">
            <label htmlFor="test-currency">Presentment currency</label>
            <select
              id="test-currency"
              className="courier-fee-input"
              value={testInput.presentment_currency}
              onChange={(e) =>
                setTestInput((prev) => ({ ...prev, presentment_currency: e.target.value }))
              }
            >
              {currencyOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <div className="courier-fee-field">
            <label htmlFor="test-goods">Goods value</label>
            <input
              id="test-goods"
              className="courier-fee-input"
              type="text"
              inputMode="decimal"
              value={testInput.goods_value}
              onChange={(e) => setTestInput((prev) => ({ ...prev, goods_value: e.target.value }))}
            />
          </div>
          <div className="courier-fee-field">
            <label htmlFor="test-consignments">Consignments</label>
            <input
              id="test-consignments"
              className="courier-fee-input"
              type="number"
              min="1"
              value={testInput.consignment_count}
              onChange={(e) =>
                setTestInput((prev) => ({ ...prev, consignment_count: e.target.value }))
              }
            />
          </div>
        </div>
        <button
          type="button"
          className="courier-fee-button courier-fee-button-secondary"
          onClick={handleTest}
          disabled={testing}
        >
          {testing ? 'Testing…' : 'Run test calculation'}
        </button>

        {testResult && (
          <div
            className={`courier-fee-result ${
              testResult.applied ? 'is-applied' : 'is-not-applied'
            }`}
          >
            <div className="courier-fee-result-title">
              {testResult.applied ? '✅ Fee would be applied' : '⚠️ Fee would not be applied'}
            </div>
            <p>{testResult.message}</p>
            <dl className="courier-fee-result-details">
              <div>
                <dt>Generic processing fee</dt>
                <dd>
                  {testResult.generic_processing_fee} {testResult.presentment_currency}
                </dd>
              </div>
              <div>
                <dt>Replaced</dt>
                <dd>
                  {testResult.replaced_generic_processing_fee} {testResult.presentment_currency}
                </dd>
              </div>
              <div>
                <dt>Collected instead</dt>
                <dd>
                  {testResult.effective_processing_fee} {testResult.presentment_currency}
                </dd>
              </div>
              <div>
                <dt>Reason</dt>
                <dd>{testResult.reason}</dd>
              </div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourierProcessingFeeSettings;
